/**
 * `downloadExport` — authenticated blob download for the export pipeline.
 *
 * Why this exists: the export download endpoint
 * (`GET /api/t/{slug}/exports/{id}/download`) is gated by `AuthMiddleware`,
 * which is **Bearer-header-only** — it has NO cookie fallback for the access
 * token (only `/auth/refresh` is cookie-authed). The access token lives in the
 * in-memory auth store and is injected as an `Authorization: Bearer …` header by
 * `apiClient`'s request interceptor.
 *
 * A plain `<a href download>` navigation does NOT carry that header, so the
 * request hits the endpoint unauthenticated → 401 JSON, which the browser saves
 * as `download.json` ("download.json file not available"). This affected EVERY
 * FE export download.
 *
 * The fix: fetch the artifact through `apiClient` (token injected + 401-refresh
 * retry handled) as a blob, then trigger a download from an object URL via a
 * transient anchor.
 *
 * `saveBlobAsFile` — write an already-fetched Blob to disk without triggering
 * the browser download manager's auto-open behaviour.
 *
 * On Chromium (Chrome/Edge) the File System Access API (`showSaveFilePicker`)
 * writes the file directly to the chosen path — the browser never registers the
 * file as a "download", so the OS auto-open-PDF-in-Adobe behaviour is bypassed.
 * On Firefox/Safari (no FSA) the classic object-URL anchor path is used as a
 * fallback.
 */
import { apiClient } from "@/lib/apiClient";

interface DownloadExportResult {
  ok: boolean;
  /** A user-facing message when `ok` is false. */
  error?: string;
}

/**
 * Parse the `filename` from a `Content-Disposition` header.
 * The backend sets `attachment; filename="<kind>_<tenant>_<id>.<ext>"`.
 * Supports both the plain `filename="…"` and RFC 5987 `filename*=UTF-8''…`
 * forms; returns `null` if neither is present.
 */
function filenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  // RFC 5987 extended form takes precedence when present.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^["']|["']$/g, "").trim());
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  if (plain?.[1]) return plain[1].trim();
  return null;
}

/** Map a download error to a short, user-facing message. */
function messageForError(err: unknown): string {
  const status = (err as { status?: number; response?: { status?: number } })?.status
    ?? (err as { response?: { status?: number } })?.response?.status;
  if (status === 410) return "This export has expired. Request a new one.";
  if (status === 409) return "The export isn't ready yet. Try again in a moment.";
  if (status === 403 || status === 404) return "You don't have access to this export.";
  return "Couldn't download the export. Try again.";
}

/**
 * Download an export artifact through the authenticated `apiClient`.
 *
 * @param tenantSlug  Tenant slug from the route.
 * @param exportId    Export UUID.
 * @param fallbackName Filename to use when the response has no
 *   `Content-Disposition` header (e.g. `candidate_writeup_<id>.pdf`).
 * @returns `{ ok: true }` on success, or `{ ok: false, error }` on failure —
 *   never saves an error blob.
 */
export async function downloadExport(
  tenantSlug: string | undefined,
  exportId: string,
  fallbackName?: string,
): Promise<DownloadExportResult> {
  if (!tenantSlug) {
    return { ok: false, error: "Couldn't download the export. Try again." };
  }

  let blob: Blob;
  let filename: string;
  try {
    const resp = await apiClient.get<Blob>(
      `/api/t/${tenantSlug}/exports/${exportId}/download`,
      { responseType: "blob" },
    );
    blob = resp.data;
    filename =
      filenameFromContentDisposition(
        resp.headers?.["content-disposition"] as string | undefined,
      ) ??
      fallbackName ??
      `export_${exportId}`;
  } catch (err) {
    return { ok: false, error: messageForError(err) };
  }

  // Trigger the download via a transient anchor + object URL.
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }

  return { ok: true };
}

/**
 * Save an already-fetched Blob to disk without triggering the browser
 * download manager's auto-open behaviour.
 *
 * Strategy:
 *  1. Chromium (Chrome/Edge): File System Access API (`showSaveFilePicker`)
 *     — writes directly to the user-chosen path; the browser never registers
 *     the file as a "download", so the OS auto-open-PDF behaviour is
 *     bypassed. User-cancel (AbortError) is treated as success (not an error).
 *  2. Firefox/Safari fallback: classic object-URL + transient anchor download
 *     — same pattern as `downloadExport`. These browsers do not auto-open
 *     PDFs by default, so the original access-denied problem does not apply.
 *
 * @param blob      The Blob to save (already fetched; this function does NOT
 *                  fetch from the network).
 * @param filename  The suggested filename shown in the save dialog.
 */
export async function saveBlobAsFile(
  blob: Blob,
  filename: string,
): Promise<{ ok: true }> {
  // File System Access API — Chromium only. Not in the standard TS DOM lib;
  // use a localized cast to avoid any-leakage into the rest of the codebase.
  if ("showSaveFilePicker" in window) {
    try {
      const handle = await (
        window as Window & {
          showSaveFilePicker(opts?: {
            suggestedName?: string;
            types?: Array<{ description: string; accept: Record<string, string[]> }>;
          }): Promise<{
            createWritable(): Promise<{
              write(data: Blob): Promise<void>;
              close(): Promise<void>;
            }>;
          }>;
        }
      ).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: "PDF document",
            accept: { "application/pdf": [".pdf"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return { ok: true };
    } catch (err) {
      // User cancelled the picker — treat as success (not an error).
      if ((err as DOMException)?.name === "AbortError") return { ok: true };
      // Any other FSA failure (permissions, disk full, etc.) — fall through
      // to the classic anchor download so the user is not left empty-handed.
    }
  }

  // Fallback: classic object-URL + transient anchor (Firefox/Safari, or FSA
  // failures). Matches the pattern in `downloadExport`.
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }

  return { ok: true };
}
